// lineSpineWidthTools.js — Bespoke draw interactions for LINE spine+width rules.
// LINE12, LINE17, LINE22, LINE23
//
// P1/P2 = spine, P3 = perpendicular width side point.
// Sketch: after P2, shows dashed parallel lines at ±width from spine.

import { DrawRules } from '@armyc2.c5isr.renderer/mil-sym-ts-web';
import Feature from 'ol/Feature';
import { LineString } from 'ol/geom';
import { registerDrawInteraction } from '../customDrawInteractions';
import { createSequentialCollector } from './sequentialCollector';
import { distanceToInfiniteLine } from './geometryUtils';
import { PREVIEW_LINE_BLUE, PREVIEW_LINE_AMBER_DASHED, PREVIEW_LINE_FAINT_BLUE_DASHED } from './drawPreviewStyles';

const SPINE_STYLE = PREVIEW_LINE_BLUE;
const WIDTH_STYLE = PREVIEW_LINE_AMBER_DASHED;
const PERP_STYLE  = PREVIEW_LINE_FAINT_BLUE_DASHED;

function offsetLine(p1, p2, nx, ny, d) {
    return [[p1[0]+nx*d, p1[1]+ny*d], [p2[0]+nx*d, p2[1]+ny*d]];
}

// P1/P2 = centerline (anchor role), P3 = width side point (width role).
function roleForIndex(i) { return i < 2 ? 'anchor' : 'width'; }

function makeSpineWidthCollector(map, opts) {
    return createSequentialCollector(map, {
        minCount: 3,
        maxCount: 3,
        onFinish: opts.onFinish,
        sidc: opts.sidc || null,
        previewMinPoints: 2,
        cursorFirst: false,
        roleForIndex,
        styleResolver: (f) => {
            const t = f.get('_t');
            if (t === 'width') return WIDTH_STYLE;
            if (t === 'perp') return PERP_STYLE;
            return SPINE_STYLE;
        },
        onSketchUpdate: ({ previewSource, collected3857, cursorCoord3857 }) => {
            if (collected3857.length >= 1) {
                const pts = cursorCoord3857 ? [...collected3857, cursorCoord3857] : collected3857;
                if (pts.length >= 2) {
                    const lf = new Feature(new LineString(pts.slice(0, 2)));
                    lf.set('_t', 'spine');
                    previewSource.addFeature(lf);
                }
            }
            // Width bracket preview after 2 pts placed
            if (collected3857.length >= 2 && cursorCoord3857) {
                const [a, b] = collected3857;
                const dx = b[0]-a[0], dy = b[1]-a[1];
                const len = Math.sqrt(dx*dx+dy*dy);
                if (len > 0) {
                    const nx = -dy/len, ny = dx/len;
                    const w = distanceToInfiniteLine(cursorCoord3857[0], cursorCoord3857[1], a[0], a[1], b[0], b[1]);
                    const l1 = offsetLine(a, b, nx, ny, w);
                    const l2 = offsetLine(a, b, nx, ny, -w);
                    [l1, l2].forEach((pts2) => {
                        const wf = new Feature(new LineString(pts2));
                        wf.set('_t', 'width');
                        previewSource.addFeature(wf);
                    });
                    // Perp line from midpoint to cursor
                    const mx = (a[0]+b[0])/2, my = (a[1]+b[1])/2;
                    const pf = new Feature(new LineString([[mx, my], cursorCoord3857]));
                    pf.set('_t', 'perp');
                    previewSource.addFeature(pf);
                }
            }
        },
    });
}

registerDrawInteraction(DrawRules.LINE12, (map, opts) => makeSpineWidthCollector(map, opts));
registerDrawInteraction(DrawRules.LINE17, (map, opts) => makeSpineWidthCollector(map, opts));
registerDrawInteraction(DrawRules.LINE22, (map, opts) => makeSpineWidthCollector(map, opts));
registerDrawInteraction(DrawRules.LINE23, (map, opts) => makeSpineWidthCollector(map, opts));
